/**
 * Strategy questionnaire page — loading/error shells and live question flow.
 *
 * Belongs here: route-level layout, wiring useStrategyQuestionnairePageFlow to ui/ components.
 * Does not belong here: flow rules, API fetch, or low-level animation logic.
 */
import React, { useEffect } from 'react'
import { getBottomPadding } from '../../../../../utils/sessionStorage'
import { EventNames } from '../../../../../types/misc.types'
import { trackEvent } from '../../../../../utils/appBridge'
import { ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { useStrategyQuestionnaireContext } from '../flow-engine/StrategyQuestionnaireProvider'
import { useStrategyQuestionnairePageFlow } from '../flow-engine/useStrategyQuestionnairePageFlow'
import { StrategyQuestionnaireHeader } from '../ui/Header'
import { StrategyQuestionnaireIntro } from '../ui/Intro'
import {
  getQuestionnaireShellBottomPadding,
  StrategyQuestionnaireBody
} from '../ui/Body'

const AllocationStrategyQuestionnairePage: React.FC = () => {
  const {
    questionnaireData,
    rawQuestionnaireData,
    isQuestionnaireLoading,
    isQuestionnaireError,
    stageIndex,
    draftAnswers,
    updateDraftAnswer,
    clearAnswer,
    cancelAnswering
  } = useStrategyQuestionnaireContext()

  const stage = questionnaireData?.stages[stageIndex]
  const handleBack = () => cancelAnswering()

  const { entry, progress, animations, handlers } = useStrategyQuestionnairePageFlow()

  const isQuestionnaireReady =
    !isQuestionnaireLoading &&
    !isQuestionnaireError &&
    questionnaireData != null &&
    rawQuestionnaireData != null &&
    stage != null

  useEffect(() => {
    if (!isQuestionnaireReady) return
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: ExpertReviewScreen.StrategyQuestionnaire }
    })
  }, [isQuestionnaireReady])

  if (isQuestionnaireLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-white px-5 py-16'>
        <p className='text-gray-600'>Loading…</p>
      </div>
    )
  }

  if (isQuestionnaireError || !questionnaireData) {
    return (
      <div className='h-screen bg-white px-5 py-6 flex flex-col'>
        <header className='shrink-0'>
          <ExpertReviewTopBar
            onBack={handleBack}
            menu='strategy'
            analyticsScreen={ExpertReviewScreen.StrategyQuestionnaire}
            testLocatorBack='strategy-questionnaire-error-back'
            testLocatorMenu='strategy-questionnaire-error-menu'
          />
        </header>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-gray-600'>Failed to load questionnaire</p>
        </div>
      </div>
    )
  }

  if (!stage || !rawQuestionnaireData) {
    return (
      <div className='h-screen bg-white px-5 py-6 flex flex-col'>
        <header className='shrink-0'>
          <ExpertReviewTopBar
            onBack={handleBack}
            menu='strategy'
            analyticsScreen={ExpertReviewScreen.StrategyQuestionnaire}
            testLocatorBack='strategy-questionnaire-empty-back'
            testLocatorMenu='strategy-questionnaire-empty-menu'
          />
        </header>
        <div className='flex-1 flex items-center justify-center'>
          <p className='text-gray-600'>No questionnaire data</p>
        </div>
      </div>
    )
  }

  // Static PNG intro before first hub journey (?isIntro=true) — separate from stage Rives
  if (entry.showStaticIntro) {
    return (
      <StrategyQuestionnaireIntro
        onBack={handleBack}
        onStart={entry.dismissStaticIntro}
      />
    )
  }

  const contentBottomPadding = getQuestionnaireShellBottomPadding()
  const isStageIntroPhase = animations.isShowingStageIntro

  return (
    <div
      className='flex h-screen flex-col min-h-0 bg-white'
      style={
        isStageIntroPhase ? undefined : { paddingBottom: `calc(${getBottomPadding()}px + 16px)` }
      }
    >
      <div className='shrink-0'>
        <StrategyQuestionnaireHeader
          rawQuestionnaireData={rawQuestionnaireData}
          totalStages={progress.questionnaireData?.stages?.length ?? 1}
          stageIndex={progress.stageIndex}
          progressInStage={progress.progressInStage}
          stageLabel={progress.stage?.displayText ?? ''}
          positionInStage={progress.questionIndex + 1}
          visibleLength={progress.visibleQuestions.length}
          hideStageLabel={animations.isShowingStageIntro}
          onBack={handlers.handleBack}
          testLocatorBack='strategy-questionnaire-back'
          testLocatorMenu='strategy-questionnaire-menu'
        />
      </div>
      <StrategyQuestionnaireBody
        bodyPhase={animations.bodyPhase}
        stageIntroRiveSrc={animations.stageIntroRiveSrc}
        stageIntroStageId={animations.stageIntroStageId}
        slideDirection={progress.slideDirection}
        onStageIntroComplete={animations.onStageIntroComplete}
        onStageCompleteRiveDone={animations.onStageCompleteRiveDone}
        questionsContent={{
          stageId: progress.stage?.id,
          currentQuestion: progress.currentQuestion,
          visibleQuestions: progress.visibleQuestions,
          draftAnswers,
          slideDirection: progress.slideDirection,
          contentBottomPadding,
          onAdvance: handlers.handleAnswerAndAdvance,
          updateDraftAnswer,
          onAmountClear: clearAnswer
        }}
      />
    </div>
  )
}

export default AllocationStrategyQuestionnairePage
